import {
    addDoc,
    collection,
    doc,
    setDoc,
    deleteDoc,
    query,
    where,
    getDocs,
    writeBatch,
    runTransaction,
} from 'firebase/firestore';
import { notifications } from "@mantine/notifications";
import { DateTime } from 'luxon';
import slugify from 'slugify';
import { FirebaseRefs } from "./firebase";
import {
    Guide,
    guideConverter,
    GuideSlugClass,
    guideSlugClassConverter,
    UsernameClass,
    usernameClassConverter,
} from './interfaces';
import { assertNonEmpty } from "./placeFunctions";
import { UserData } from './userdata';

/**
 * Updates a guide with the partial values provided,
 * merging with whatever's in the database.
 */
export async function updateGuide(
    firebaseRefs: FirebaseRefs,
    guideId: string,
    userData: UserData,
    guideFields: Partial<Guide>) {
    assertNonEmpty(guideId, 'guideId');
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    const guide: Partial<Guide> = {
        ...guideFields,
        updatedAt: DateTime.now(),
    };
    const ref = doc(firebaseRefs.firestore, 'users', userId, 'guides', guideId)
        .withConverter(guideConverter);
    await setDoc(ref, guide, { merge: true });
}

/**
 * Inserts a guide with the partial values provided.
 */
export async function insertGuide(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    guideFields: Partial<Guide>
): Promise<string> {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    const guide: Partial<Guide> = {
        ...guideFields,
        creatorId: userId,
        createdAt: DateTime.now(),
        schemaVersion: 1,
    };

    const ref = collection(firebaseRefs.firestore, 'users', userId, 'guides')
        .withConverter(guideConverter);

    const docRef = await addDoc(ref, guide);
    return docRef.id; // <- pay back the docid
}

/**
 * Inserts a username doc.
 */
export async function insertUsernameDoc(
    firebaseRefs: FirebaseRefs,
    userData: UserData,
    username: string,
) {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');
    const usernameDoc: UsernameClass = {
        uid: userId,
        createdAt: DateTime.now(),
        username,
    };

    const ref = doc(firebaseRefs.firestore, 'usernames', username)
        .withConverter(usernameClassConverter);

    await runTransaction(firebaseRefs.firestore, async (transaction) => {
        const existing = await transaction.get(ref);
        if (existing.exists()) {
            const existingUid = existing.data()?.uid;
            if (existingUid === userId) {
                // Already owned by this user — no-op
                return;
            }
            throw new Error(`Username "${username}" is already taken by another user.`);
        }
        transaction.set(ref, usernameDoc);
    });
}

/**
 * Inserts a guideSlug doc.
 */
export async function insertGuideSlug(
    firebaseRefs: FirebaseRefs,
    guideDocid: string | null | undefined,
    guideTitle: string,
    userId: string | undefined,
) {
    const uid = assertNonEmpty(userId, 'userId');
    const title = assertNonEmpty(guideTitle, 'guideTitle');
    const guideId = assertNonEmpty(guideDocid, 'guideDocid');
    const guideSlug = assertNonEmpty(
        generateSlugFromTitle(title) ?? `guide-${guideId}`,
        'guideSlug'
    );

    const guideSlugDoc: GuideSlugClass = {
        guideSlug,
        guideId,
        createdAt: DateTime.now(),
    };

    const ref = doc(firebaseRefs.firestore, 'users', uid, 'guideSlugs', guideSlug)
        .withConverter(guideSlugClassConverter);

    await runTransaction(firebaseRefs.firestore, async (transaction) => {
        const existing = await transaction.get(ref);
        if (existing.exists()) {
            const existingGuideId = existing.data()?.guideId;
            if (existingGuideId === guideId) {
                // Same guide updating its own slug — overwrite is fine
                transaction.set(ref, guideSlugDoc);
                return;
            }
            throw new Error(
                `Slug "${guideSlug}" is already used by another guide. Please choose a different title.`
            );
        }
        transaction.set(ref, guideSlugDoc);
    });
}

/**
 * Delete guideSlug docs from guideID.
 */
export async function deleteGuideSlugDocsFromGuideId(
    firebaseRefs: FirebaseRefs,
    guideDocid: string | null | undefined,
    user: UserData, // currently unused, see comment below
) {
    // 1. Validate inputs
    const validGuideId = assertNonEmpty(guideDocid, 'guideDocid');
    const userId = assertNonEmpty(user.firebaseUserId, 'userData.firebaseUserId');
    const ref = collection(firebaseRefs.firestore, 'users', userId, 'guideSlugs')
        .withConverter(guideSlugClassConverter);
    // Query all docs whose guideId matches the given guideDocid
    const q = query(ref, where('guideId', '==', validGuideId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
        // nothing to delete
        return;
    }

    // Batch delete all matching docs
    const batch = writeBatch(firebaseRefs.firestore);
    snapshot.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref);
    });

    await batch.commit();
}

// delete Guide in firestore
export const deleteGuide = async (
    firebaseRefs: FirebaseRefs,
    guide: Partial<Guide>,
    userData: UserData
) => {
    const userId = assertNonEmpty(userData.firebaseUserId, 'userData.firebaseUserId');

    if (guide.docid) {
        await deleteGuideSlugDocsFromGuideId(firebaseRefs, guide.docid, userData);
        const tRef = doc(firebaseRefs.firestore, 'users', userId, 'guides', guide.docid);
        await deleteDoc(tRef);

        notifications.show({
            title: 'Guide Deleted',
            message: 'Your guide is removed',
            autoClose: true,
        });
    }
};

export function generateSlugFromTitle(title: string | null | undefined): string | null {
    if (!title) return null;
    const rawSlug = slugify(title, {
        lower: true,
        strict: true, // remove characters that are not letters, numbers, or hyphens
        trim: true,
    });
    // Enforce a maximum length for the slug to keep URLs manageable
    const MAX_SLUG_LENGTH = 80;
    const truncatedSlug = rawSlug.slice(0, MAX_SLUG_LENGTH);
    // Ensure we never return an empty string; fall back to a generic slug if needed
    return truncatedSlug || null;
}

export async function copyUrl(username? : string | null, slug? : string | null) {
    if (!username || !slug) {
        notifications.show({
            title: "Error",
            message: "Unable to generate guide link",
            color: "red",
            autoClose: true,
        });
        return;
    }

    const urlCopy = `https://app.travelscroll.com/guides/${username}/${slug}`;

    try {
        await navigator.clipboard.writeText(urlCopy);
        notifications.show({
            title: "Done",
            message: "Copied Guide link to clipboard!",
            autoClose: true,
        });
    } catch (err) {
        notifications.show({
            title: "Clipboard blocked",
            message: "Could not copy to clipboard. Check browser permissions.",
            color: "red",
            autoClose: true,
        });
    }
}
